import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { whatsappLink } from "@/config/site";

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-gradient-to-b from-stone-50 via-white to-stone-50">
      {/* Decorative orbs */}
      <div aria-hidden className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-bordeaux-100/60 blur-3xl" />
      <div aria-hidden className="absolute top-40 -right-24 h-80 w-80 rounded-full bg-navy-100/60 blur-3xl" />
      <div aria-hidden className="absolute inset-0 grain opacity-40" />

      <div className="container-tight relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="inline-flex flex-wrap items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-bordeaux-50 border border-bordeaux-100 text-bordeaux-700 text-xs font-semibold uppercase tracking-[0.18em]">
                <Sparkles size={14} /> Academia de Estudos
              </span>
              <span className="inline-flex flex-col items-start px-4 py-1.5 rounded-full bg-navy-50 border border-navy-100 text-navy-700 text-xs font-semibold uppercase tracking-[0.18em] leading-tight">
                <span>Instituto de Formação</span>
                <span className="text-[9px] font-medium tracking-[0.14em] text-navy-500 normal-case">Entidade certificada pela DGERT</span>
              </span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-stone-900 leading-[1.05] tracking-tight text-balance">
              Excelência Académica
              <br />
              <span className="text-bordeaux-700 italic">e Desenvolvimento</span> Pessoal
            </h1>

            <p className="mt-7 text-base sm:text-lg text-stone-600 max-w-2xl leading-relaxed text-pretty">
              Acompanhamento personalizado, preparação para exames, apoio emocional e ensino Cambridge —
              para ajudar cada aluno a atingir o seu máximo potencial.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-bordeaux-700 text-white font-semibold hover:bg-bordeaux-800 transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(124,31,44,0.22)]"
              >
                <MessageCircle size={18} /> Falar no WhatsApp
              </a>
              <a
                href="#contacto"
                data-testid="hero-contact-btn"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-navy-700 text-navy-700 font-semibold hover:bg-navy-700 hover:text-white transition-all"
              >
                Entrar em Contacto <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "+300", v: "Alunos acompanhados" },
                { k: "12+", v: "Anos de experiência" },
                { k: "97%", v: "Taxa de aprovação" },
              ].map((s) => (
                <div key={s.k} data-testid={`hero-stat-${s.k}`}>
                  <dt className="font-display text-3xl text-bordeaux-700">{s.k}</dt>
                  <dd className="text-xs sm:text-sm text-stone-600 mt-1 leading-snug">{s.v}</dd>
                </div>
              ))}
            </dl>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(28,25,23,0.18)] aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/8617843/pexels-photo-8617843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Aluna do secundário a estudar no Instituto Leopoldo Guimarães"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bordeaux-900/40 via-transparent to-transparent" />
            </div>

            {/* Floating badge card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute -left-6 bottom-10 sm:-left-10 bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] p-5 max-w-[260px] border border-stone-100 animate-gentle-float"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-bordeaux-50 flex items-center justify-center text-bordeaux-700 font-display text-lg">★</div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold">Cambridge</div>
                  <div className="text-sm font-semibold text-stone-900">Preparação Especializada</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
